import {
  useQuery,
  QueryKey,
  QueryObserverOptions,
  UseQueryResult,
  MutationKey,
  useMutation,
  MutateOptions,
} from "react-query";

import {
  organizationChartCreateDeptManagerService,
  organizationChartCreateDeptService,
  organizationChartUpdateDeptManagerService,
  organizationChartUpdateDeptService,
  organizationChartDeleteDeptManagerService,
  organizationChartListService,
  organizationChartEmployeeListService,
  organizationChartDeleteDeptService,
} from "./services";

type OrganizationChartListResponse = Awaited<
  ReturnType<typeof organizationChartListService>
>;

export const useOrganizationChart = ({
  queryKey,
  options,
}: {
  queryKey?: QueryKey | string;
  options?: QueryObserverOptions<
    unknown,
    unknown,
    OrganizationChartListResponse
  >;
}) => {
  const response = useQuery({
    queryKey: queryKey ?? "organizationChartHooks",
    queryFn: () => organizationChartListService(),
    ...options,
  });

  return response as UseQueryResult<OrganizationChartListResponse>;
};

export const useDeptCreate = ({
  queryKey,
  options,
}: {
  queryKey?: MutationKey | string;
  options?: MutateOptions<
    Awaited<ReturnType<typeof organizationChartCreateDeptService>>,
    Parameters<typeof organizationChartCreateDeptService>
  >;
}) => {
  const response = useMutation({
    mutationKey: queryKey ?? "organizationChartCreateDeptServiceHooks",
    mutationFn: organizationChartCreateDeptService,
    onSuccess: options?.onSuccess as unknown as () => void,
    onError: options?.onError as unknown as () => void,
  });

  return response;
};

export const useDeptUpdate = ({
  queryKey,
  options,
}: {
  queryKey?: MutationKey | string;
  options?: MutateOptions<
    Awaited<ReturnType<typeof organizationChartUpdateDeptService>>,
    Parameters<typeof organizationChartUpdateDeptService>
  >;
}) => {
  const response = useMutation({
    mutationKey: queryKey ?? "organizationChartUpdateDeptServiceHooks",
    mutationFn: organizationChartUpdateDeptService,
    onSuccess: options?.onSuccess as unknown as () => void,
    onError: options?.onError as unknown as () => void,
  });

  return response;
};

export const useDeptDelete = ({
  queryKey,
  options,
}: {
  queryKey?: MutationKey | string;
  options?: MutateOptions<
    Awaited<ReturnType<typeof organizationChartDeleteDeptService>>,
    unknown,
    Parameters<typeof organizationChartDeleteDeptService>[0]
  >;
}) => {
  const response = useMutation({
    mutationKey: queryKey ?? "organizationChartDeleteDeptServiceHooks",
    mutationFn: organizationChartDeleteDeptService,
    onSuccess: options?.onSuccess as unknown as () => void,
    onError: options?.onError as unknown as () => void,
  });

  return response;
};

export const useDeptManagerCreate = ({
  queryKey,
  options,
}: {
  queryKey?: MutationKey | string;
  options?: MutateOptions<
    Awaited<ReturnType<typeof organizationChartCreateDeptManagerService>>,
    unknown,
    Parameters<typeof organizationChartCreateDeptManagerService>[0]
  >;
}) => {
  const response = useMutation({
    mutationKey: queryKey ?? "organizationChartCreateDeptManagerServiceHooks",
    mutationFn: organizationChartCreateDeptManagerService,
    onSuccess: options?.onSuccess as unknown as () => void,
    onError: options?.onError as unknown as () => void,
  });

  return response;
};

export const useDeptManagerUpdate = ({
  queryKey,
  options,
}: {
  queryKey?: MutationKey | string;
  options?: MutateOptions<
    Awaited<ReturnType<typeof organizationChartUpdateDeptManagerService>>,
    unknown,
    Parameters<typeof organizationChartUpdateDeptManagerService>[0]
  >;
}) => {
  const response = useMutation({
    mutationKey: queryKey ?? "organizationChartUpdateDeptManagerServiceHooks",
    mutationFn: organizationChartUpdateDeptManagerService,
    onSuccess: options?.onSuccess as unknown as () => void,
    onError: options?.onError as unknown as () => void,
  });

  return response;
};

export const useDeptManagerDelete = ({
  queryKey,
  options,
}: {
  queryKey?: MutationKey | string;
  options?: MutateOptions<
    Awaited<ReturnType<typeof organizationChartDeleteDeptManagerService>>,
    unknown,
    Parameters<typeof organizationChartDeleteDeptManagerService>[0]
  >;
}) => {
  const response = useMutation({
    mutationKey: queryKey ?? "organizationChartDeleteDeptManagerService",
    mutationFn: organizationChartDeleteDeptManagerService,
    onSuccess: options?.onSuccess as unknown as () => void,
    onError: options?.onError as unknown as () => void,
  });

  return response;
};

export const useDeptEmployees = ({
  queryKey,
  options,
}: {
  queryKey?: MutationKey | string;
  options?: MutateOptions<
    Awaited<ReturnType<typeof organizationChartEmployeeListService>>,
    unknown,
    Parameters<typeof organizationChartEmployeeListService>[0]
  >;
}) => {
  const response = useMutation({
    mutationKey: queryKey ?? "organizationChartEmployeeListServiceHooks",
    mutationFn: organizationChartEmployeeListService,
    onSuccess: options?.onSuccess as unknown as () => void,
    onError: options?.onError as unknown as () => void,
  });

  return response;
};
